import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import {
  AppCustomerCompositeEntity,
  AppFeatureEntity,
  AppFeaturePackageCustomerAccessComposite,
  AppFeaturePackageCustomerAccessEntity,
  AppFeaturePackageEntity,
  AppFeaturePackageKey,
  AppFeaturePackageMembershipAssignmentEntity,
  AppMembershipCompositeEntity,
  FeaturePackageDataNested,
} from '@nk/data-entities-lib';
import { arrayToDict, arrayUnique, Dict } from '@nk/utils-lib';

export interface AvailableAppFeaturePackageItem {
  uuid: string;
  data: AppFeaturePackageCustomerAccessComposite;
  appFeatures: AppFeatureEntity[];
  groupName: string;
  groupPriority: number;
  checked: boolean;
  disabled: boolean;
}

export interface AppFeaturePackageGroup {
  groupName: string;
  groupPriority: number;
  featurePackages: AvailableAppFeaturePackageItem[];
}

export const CUSTOMER_SUBSCRIPTION_TYPES: AppFeaturePackageKey[] = [
  AppFeaturePackageKey.app_trial,
  AppFeaturePackageKey.app_enterprise,
];

export const ALL_POSSIBLE_CUSTOMER_SUBSCRIPTION_TYPES: AppFeaturePackageKey[] = [
  AppFeaturePackageKey.app_trial,
  AppFeaturePackageKey.app_enterprise,
  AppFeaturePackageKey.app_basic,
  AppFeaturePackageKey.app_pro,
  AppFeaturePackageKey.app_pro_news,
];

export const COMMON_FEATURE_PACKAGES = new Set([
  AppFeaturePackageKey.nordic_beta,
  AppFeaturePackageKey.norwegian_mortgaged_movable_property_data,
  AppFeaturePackageKey.network_risk_score,
  AppFeaturePackageKey.fraud_risk_score,
  AppFeaturePackageKey.adverse_event,
  AppFeaturePackageKey.payment_remark_company_overview,
  AppFeaturePackageKey.payment_remark,
  AppFeaturePackageKey.norwegian_property_data,
  AppFeaturePackageKey.bank_account_prevalidation,
]);

export const API_FEATURE_PACKAGES = new Set([AppFeaturePackageKey.analysis_api, AppFeaturePackageKey.datasets_api]);

export const HIDDEN_FEATURE_PACKAGES = new Set([
  AppFeaturePackageKey.experimental_flags,
  AppFeaturePackageKey.corpia_reports,
  AppFeaturePackageKey.indicator_flags,
  AppFeaturePackageKey.customer_santander_flags,
  AppFeaturePackageKey.screening_beta, // Screening beta role is hidden for now, Ronja or the tech team can give screening feature manually per request
]);

export const SUBSCRIPTION_GROUP_PRIORITY = 4;

function findSubUnusedFeaturePackages(
  featurePackageNested: FeaturePackageDataNested[],
  appFeaturePackageCustomerAccesses: AppFeaturePackageCustomerAccessEntity[],
  appFeaturePackageMembershipAssignments: AppFeaturePackageMembershipAssignmentEntity[],
): FeaturePackageDataNested[] {
  const featurePackagesInUse = appFeaturePackageCustomerAccesses
    .filter((customerAccess) => {
      return appFeaturePackageMembershipAssignments.find(
        (assignments) => assignments.app_feature_package_customer_access_uuid === customerAccess.uuid,
      );
    })
    .filter((customerAccess) => !customerAccess.app_subscription_uuid)
    .map((a) => a.app_feature_package_uuid);

  let featurePackages = featurePackageNested;

  if (featurePackagesInUse.length) {
    featurePackages = featurePackages.filter((a) => {
      return !featurePackagesInUse.includes(a.appFeaturePackage.uuid);
    });
  }

  const subscriptionBased = ALL_POSSIBLE_CUSTOMER_SUBSCRIPTION_TYPES;

  featurePackages = featurePackages.filter(
    (a) => !subscriptionBased.includes(a.appFeaturePackage.app_feature_package_key),
  );

  return featurePackages;
}

export interface FeaturePackageGroup {
  groupName: string;
  featurePackages: FeaturePackageDataNested[];
}

export function sortFeaturePackagesAlphabetically(
  a: AppFeaturePackageEntity,
  b: AppFeaturePackageEntity,
  translateService: TranslateService,
): number {
  const aTitle =
    translateService.instant('app_feature_package_key.' + a.app_feature_package_key) || a.app_feature_package_key;
  const bTitle =
    translateService.instant('app_feature_package_key.' + b.app_feature_package_key) || b.app_feature_package_key;
  return aTitle.localeCompare(bTitle);
}

export function findUnusedFeaturePackages(
  featurePackageNested: FeaturePackageDataNested[],
  appFeaturePackageCustomerAccesses: AppFeaturePackageCustomerAccessEntity[],
  translateService: TranslateService,
  ...appFeaturePackageMembershipAssignmentArrays: AppFeaturePackageMembershipAssignmentEntity[][]
): FeaturePackageGroup[] {
  let possibleFeaturePackages: FeaturePackageDataNested[] = [];
  for (const appFeaturePackageMembershipAssignments of appFeaturePackageMembershipAssignmentArrays) {
    const featurePackages = findSubUnusedFeaturePackages(
      featurePackageNested,
      appFeaturePackageCustomerAccesses,
      appFeaturePackageMembershipAssignments,
    );
    possibleFeaturePackages = possibleFeaturePackages.concat(featurePackages);
  }
  possibleFeaturePackages = possibleFeaturePackages.filter(
    (i) => !HIDDEN_FEATURE_PACKAGES.has(i.appFeaturePackage.app_feature_package_key),
  );
  const uniquePackages = arrayUnique(possibleFeaturePackages, (i) => i.appFeaturePackage.uuid);
  const commonFeaturePackageGroup: FeaturePackageGroup = {
    groupName: extractTranslation('feature.feature_package.group.common'),
    featurePackages: uniquePackages
      .filter((i) => COMMON_FEATURE_PACKAGES.has(i.appFeaturePackage.app_feature_package_key))
      .sort((a, b) => sortFeaturePackagesAlphabetically(a.appFeaturePackage, b.appFeaturePackage, translateService)),
  };
  const apiFeaturePackageGroup: FeaturePackageGroup = {
    groupName: extractTranslation('feature.feature_package.group.api'),
    featurePackages: uniquePackages
      .filter((i) => API_FEATURE_PACKAGES.has(i.appFeaturePackage.app_feature_package_key))
      .sort((a, b) => sortFeaturePackagesAlphabetically(a.appFeaturePackage, b.appFeaturePackage, translateService)),
  };
  const restFeaturePackageGroup: FeaturePackageGroup = {
    groupName: extractTranslation('feature.feature_package.group.rest'),
    featurePackages: uniquePackages
      .filter(
        (i) =>
          !COMMON_FEATURE_PACKAGES.has(i.appFeaturePackage.app_feature_package_key) &&
          !API_FEATURE_PACKAGES.has(i.appFeaturePackage.app_feature_package_key),
      )
      .sort((a, b) => sortFeaturePackagesAlphabetically(a.appFeaturePackage, b.appFeaturePackage, translateService)),
  };

  return [commonFeaturePackageGroup, apiFeaturePackageGroup, restFeaturePackageGroup];
}

export function getGivenFeaturePackages(
  appFeatureMembershipPackageAssignments: AppFeaturePackageMembershipAssignmentEntity[],
  appCustomerComposite: AppCustomerCompositeEntity,
  appFeaturePackageDataNested: FeaturePackageDataNested[],
  excludeMainFeaturePackage = false,
): {
  featurePackageAssignment: AppFeaturePackageMembershipAssignmentEntity;
  appFeaturePackageComposite: FeaturePackageDataNested;
  featurePackageCustomerAccess: AppFeaturePackageCustomerAccessEntity;
}[] {
  const appFeaturePackageDataNestedDict = arrayToDict(appFeaturePackageDataNested, (i) => i.appFeaturePackage.uuid);
  let items = appFeatureMembershipPackageAssignments
    .map((featurePackageAssignment) => {
      const featurePackageCustomerAccess = appCustomerComposite.app_feature_package_customer_accesses.find(
        (a) => a.uuid == featurePackageAssignment.app_feature_package_customer_access_uuid,
      );
      const appFeaturePackageComposite =
        appFeaturePackageDataNestedDict[featurePackageCustomerAccess?.app_feature_package_uuid];
      return {
        appFeaturePackageComposite,
        featurePackageCustomerAccess,
        featurePackageAssignment,
      };
    })
    .filter((i) => i.featurePackageCustomerAccess && i.featurePackageAssignment)
    .sort((a, b) => {
      if (
        a.featurePackageCustomerAccess.app_subscription_uuid !== b.featurePackageCustomerAccess.app_feature_package_uuid
      ) {
        if (a.featurePackageCustomerAccess.app_subscription_uuid) {
          return -1;
        }
      }
      return (
        ALL_POSSIBLE_CUSTOMER_SUBSCRIPTION_TYPES.includes(
          a.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key,
        ) &&
        a.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key.localeCompare(
          b.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key,
        )
      );
    });

  if (excludeMainFeaturePackage) {
    items = items
      .filter(
        (i) =>
          !ALL_POSSIBLE_CUSTOMER_SUBSCRIPTION_TYPES.includes(
            i.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key,
          ),
      )
      .sort((a, b) => {
        if (
          a.featurePackageCustomerAccess.app_subscription_uuid !==
          b.featurePackageCustomerAccess.app_feature_package_uuid
        ) {
          if (a.featurePackageCustomerAccess.app_subscription_uuid) {
            return -1;
          }
        }
        return a.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key.localeCompare(
          b.appFeaturePackageComposite.appFeaturePackage.app_feature_package_key,
        );
      });
  }
  return items;
}

/**
 *
 * @param featurePackageUuid - feature package to assign
 * @param appCustomerComposite
 * @param appMembershipComposites - all membership for a provided customer
 * @param featurePackageDataNested - all feature packages we have
 */
export function getAppMembershipUuidsForPackageAssignment(
  featurePackageUuid: string,
  appCustomerComposite: AppCustomerCompositeEntity,
  appMembershipComposites: AppMembershipCompositeEntity[],
  featurePackageDataNested: FeaturePackageDataNested[],
  translateService: TranslateService,
): string[] {
  return appMembershipComposites
    .filter((appMembershipComposite) => {
      const possibleFeaturePackageGroups = findUnusedFeaturePackages(
        featurePackageDataNested,
        appCustomerComposite.app_feature_package_customer_accesses,
        translateService,
        appMembershipComposite.app_feature_package_membership_assignment_composites.map(
          (i) => i.app_feature_package_membership_assignment,
        ),
      );
      return possibleFeaturePackageGroups
        .reduce((res, i) => {
          res = res.concat(i.featurePackages);
          return res;
        }, [])
        .map((i) => i.appFeaturePackage.uuid)
        .includes(featurePackageUuid);
    })
    .map((i) => i.app_membership.uuid);
}

const FEATURE_PACKAGE_GROUP_PRIORITY = {
  /* eslint-disable @typescript-eslint/naming-convention */
  1: extractTranslation('feature.feature_package.group.common'),
  2: extractTranslation('feature.feature_package.group.api'),
  3: extractTranslation('feature.feature_package.group.rest'),
  4: extractTranslation('feature.feature_package.group.main'),
  /* eslint-enable */
};

export function getAppFeaturePackageCustomerAccessComposites(
  appFeaturePackageDict: Dict<AppFeaturePackageEntity>,
  appFeaturePackageCustomerAccesses: AppFeaturePackageCustomerAccessEntity[],
  featurePackageDataNested: FeaturePackageDataNested[],
  translateService: TranslateService,
): AvailableAppFeaturePackageItem[] {
  return appFeaturePackageCustomerAccesses
    .filter(
      (i) => !HIDDEN_FEATURE_PACKAGES.has(appFeaturePackageDict[i.app_feature_package_uuid]?.app_feature_package_key),
    )
    .map((item) => {
      const defaultFeaturePackageKeys = [AppFeaturePackageKey.nordic_beta];
      const appFeaturePackage = appFeaturePackageDict[item.app_feature_package_uuid];
      const appFeatures =
        featurePackageDataNested
          .find((i) => i.appFeaturePackage.uuid === item.app_feature_package_uuid)
          ?.appFeatures.sort((a, b) => a.feature_name.localeCompare(b.feature_name)) || [];
      const checked = Boolean(
        item.app_subscription_uuid || defaultFeaturePackageKeys.includes(appFeaturePackage.app_feature_package_key),
      );
      let groupPriority = 3;
      if (ALL_POSSIBLE_CUSTOMER_SUBSCRIPTION_TYPES.includes(appFeaturePackage.app_feature_package_key)) {
        groupPriority = 4;
      }
      if (COMMON_FEATURE_PACKAGES.has(appFeaturePackage.app_feature_package_key)) {
        groupPriority = 1;
      }
      if (API_FEATURE_PACKAGES.has(appFeaturePackage.app_feature_package_key)) {
        groupPriority = 2;
      }

      return {
        uuid: item.uuid,
        data: {
          app_subscription_composite: null,
          app_feature_package_customer_access: item,
          app_feature_package: appFeaturePackage,
        },
        appFeatures,
        checked,
        groupName: FEATURE_PACKAGE_GROUP_PRIORITY[groupPriority] || '',
        groupPriority,
        disabled: checked,
      };
    })
    .sort((a, b) =>
      sortFeaturePackagesAlphabetically(a.data.app_feature_package, b.data.app_feature_package, translateService),
    );
}
